import { useState } from "react";
import { Link } from "react-router-dom";
import { Page } from "../components/AppShell";
import { Badge, Card, Pager, PanelState } from "../components/ui";
import { dateTime, titleCase } from "../lib/format";
import { useApi } from "../lib/useApi";
import type { AuditRow, PageMeta } from "../lib/types";

const ACTIONS = [
  { value: "", label: "All actions" },
  { value: "user.revoke_all_sessions", label: "Customer signed out" },
  { value: "session.revoke", label: "Session ended" },
  { value: "user.block", label: "Customer blocked" },
  { value: "user.unblock", label: "Block lifted" },
  { value: "user.delete", label: "Customer deleted" },
  { value: "admin.invite", label: "Admin invited" },
  { value: "admin.invite_revoke", label: "Invitation revoked" },
  { value: "admin.disable", label: "Admin disabled" },
  { value: "admin.enable", label: "Admin re-enabled" },
  { value: "admin.role_change", label: "Role changed" },
  { value: "admin.password_change", label: "Password changed" },
];

function toneFor(action: string): "neutral" | "warning" | "critical" {
  if (action === "user.delete") return "critical";
  // A block stops a real person using the product, and the permanent kind is
  // the most consequential reversible thing this console does.
  if (action === "user.block") return "critical";
  if (action.startsWith("user.") || action === "admin.disable") return "warning";
  // An invitation hands someone the console. It is not destructive, but it is
  // the row you scan for when asking how a stranger got in.
  if (action === "admin.invite") return "warning";
  return "neutral";
}

/**
 * Reads are not logged — the whole console is a read, and logging every chart
 * view would bury the rows that matter. What is here is everything that
 * CHANGED something: who ended whose session, who deleted whom, and when.
 */
export function AuditPage() {
  const [action, setAction] = useState("");
  const [page, setPage] = useState(1);

  const { data, error, loading, stale } = useApi<{
    audit: AuditRow[];
    pagination: PageMeta;
  }>("/api/admin/audit", {
    action: action || undefined,
    page,
    pageSize: 50,
  });

  return (
    <Page
      title="Audit log"
      subtitle="Every action that changed something"
      actions={
        <select
          className="select"
          value={action}
          onChange={(e) => {
            setAction(e.target.value);
            setPage(1);
          }}
          aria-label="Filter by action"
        >
          {ACTIONS.map((a) => (
            <option key={a.value} value={a.value}>
              {a.label}
            </option>
          ))}
        </select>
      }
    >
      <Card
        title="Recent activity"
        note="Analytics reads are not recorded — only actions with an effect. The admin's email is stored on the row itself, so the log still names someone after an account is removed."
      >
        <div className={stale ? "stale" : undefined}>
          <PanelState
            loading={loading}
            error={error}
            empty={data?.audit.length === 0}
            emptyText="Nothing has been changed yet."
          >
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>When</th>
                    <th>Admin</th>
                    <th>Action</th>
                    <th>Target</th>
                    <th>Detail</th>
                    <th>From</th>
                  </tr>
                </thead>
                <tbody>
                  {data?.audit.map((row) => (
                    <tr key={row.id}>
                      <td className="cell-mute" style={{ whiteSpace: "nowrap" }}>
                        {dateTime(row.createdAt)}
                      </td>
                      <td className="cell-strong">{row.adminEmail}</td>
                      <td>
                        <Badge tone={toneFor(row.action)}>
                          {ACTIONS.find((a) => a.value === row.action)?.label ??
                            titleCase(row.action.replace(/\./g, " "))}
                        </Badge>
                      </td>
                      <td className="mono cell-mute">
                        {row.targetType === "user" && row.targetId ? (
                          <Link
                            to={`/customers/${row.targetId}`}
                            style={{ textDecoration: "underline" }}
                          >
                            {row.targetId}
                          </Link>
                        ) : (
                          (row.targetId ?? "—")
                        )}
                      </td>
                      <td className="cell-mute">
                        {row.detail ? summarise(row.detail) : "—"}
                      </td>
                      <td className="mono cell-mute">{row.ip ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pager
              meta={data?.pagination}
              onChange={setPage}
              busy={stale}
              label="entries"
            />
          </PanelState>
        </div>
      </Card>
    </Page>
  );
}

/** `{ sessionsEnded: 3, reason: "x" }` → `sessionsEnded: 3 · reason: x` */
function summarise(detail: Record<string, unknown>): string {
  return (
    Object.entries(detail)
      .filter(([, v]) => v !== null && v !== undefined && v !== "")
      .map(([k, v]) => `${k}: ${String(v)}`)
      .join(" · ") || "—"
  );
}
